/**
 * Renderer — owns the Three.js scene, camera, render loop, and the world.
 *
 * Phase-complete responsibilities:
 *   - Three.js scene + camera + WebGL2 (with WebGPU-soft fallback)
 *   - Quality preset detection
 *   - Day/night Sky with the festival clock
 *   - World composition (Layout)
 *   - Local player + remote-player avatars
 *   - Crowd system (NPCs)
 *   - Stage screens (CSS3D YouTube overlay)
 *   - Audio engine
 *   - Networking (ZoneClient + zone handoff)
 */

import * as THREE from 'three';
import { Sky } from '@/world/Sky';
import { buildWorld, type BuiltWorld } from '@/world/Layout';
import { PhysicsWorld } from '@/core/PhysicsWorld';
import { Input } from '@/core/Input';
import { PlayerController } from '@/player/PlayerController';
import { Avatar } from '@/player/Avatar';
import { StageScreens } from '@/video/StageScreen';
import { AudioEngine } from '@/audio/AudioEngine';
import { ZoneClient } from '@/net/ZoneClient';
import { RemoteAvatars } from '@/net/RemoteAvatars';
import { autoDetectQuality, type QualityProfile } from '@/core/QualityPresets';
import { PostFX } from '@/core/PostFX';
import { festivalClock } from '@/core/Time';
import { defaultSchedule } from '@shared/schedule';
import { ZONES, zoneAt } from '@shared/zones';
import { useUI } from '@/state/store';
import { formatFestivalTime } from '@shared/festival-clock';

export interface RendererOptions {
  wsUrlBase: string;
}

export class Renderer {
  readonly canvas: HTMLCanvasElement;
  readonly scene = new THREE.Scene();
  readonly camera: THREE.PerspectiveCamera;
  readonly webgl: THREE.WebGLRenderer;
  readonly input = new Input();

  quality: QualityProfile;
  sky!: Sky;
  physics!: PhysicsWorld;
  world!: BuiltWorld;
  player!: PlayerController;
  screens!: StageScreens;
  audio!: AudioEngine;
  net: ZoneClient;
  remotes!: RemoteAvatars;
  postFx!: PostFX;

  private clock = new THREE.Clock();
  private running = false;
  private currentZoneId: string | null = null;
  private audioTickAcc = 0;
  private skyTickAcc = 0;

  constructor(canvas: HTMLCanvasElement, opts: RendererOptions) {
    this.canvas = canvas;
    this.camera = new THREE.PerspectiveCamera(60, 1, 0.1, 5000);

    this.webgl = new THREE.WebGLRenderer({
      canvas,
      antialias: true,
      powerPreference: 'high-performance'
    });
    const gl = this.webgl.getContext() as WebGL2RenderingContext;
    this.quality = autoDetectQuality(gl);
    this.webgl.setPixelRatio(this.quality.pixelRatio);
    this.webgl.outputColorSpace = THREE.SRGBColorSpace;
    this.webgl.toneMapping = THREE.ACESFilmicToneMapping;
    this.webgl.toneMappingExposure = 1.05;
    this.webgl.shadowMap.enabled = true;
    this.webgl.shadowMap.type = THREE.PCFSoftShadowMap;

    this.net = new ZoneClient(opts.wsUrlBase);
  }

  async init(): Promise<void> {
    useUI.getState().setLoading(0.05, 'Tuning the PA…');

    this.sky = new Sky();
    this.scene.add(this.sky.mesh);
    this.scene.add(this.sky.hemi);
    this.scene.add(this.sky.sun);
    this.scene.fog = this.sky.fog;
    useUI.getState().setLoading(0.15, 'Pouring the foundations…');

    this.physics = new PhysicsWorld(null as never);
    this.world = await buildWorld(this.physics);
    this.physics.terrain = this.world.terrain;
    this.scene.add(this.world.scene);
    useUI.getState().setLoading(0.55, 'Raising the stages…');

    // Audio engine — needs the camera as the listener anchor.
    this.audio = new AudioEngine(this.camera);
    this.audio.setSchedule(defaultSchedule());
    for (const stage of Object.values(this.world.stages)) {
      this.audio.registerStage(stage);
    }
    useUI.getState().setLoading(0.7, 'Cueing the first track…');

    // Stage screens (CSS3D iframe layer)
    const screenRoot = document.getElementById('css3d-root') as HTMLDivElement;
    this.screens = new StageScreens(screenRoot, this.camera);
    for (const stage of Object.values(this.world.stages)) {
      this.screens.bind(stage);
    }
    useUI.getState().setLoading(0.8, 'Hanging the jumbotrons…');

    useUI.getState().setLoading(0.92, 'Almost there…');

    // Local player avatar + controller
    const id = useUI.getState().identity ?? { displayName: 'Bonnaroovian', hatColor: 0 };
    const avatar = Avatar.build({
      hatColor: id.hatColor,
      name: id.displayName
    });
    this.scene.add(avatar.group);
    this.player = new PlayerController(this.input, this.physics, this.camera, avatar);

    // Remote avatars
    this.remotes = new RemoteAvatars(this.scene, this.net);

    // Known zones for the spawn picker / minimap
    useUI.getState().setKnownZones(Object.values(ZONES));
    useUI.getState().setLoading(1, 'Ready');

    // Run one sky + player update before the first render so the very first
    // frame is fully lit. Without this, sky uniforms start at black and the
    // camera lerps from origin, causing a black flash on boot.
    this.sky.update(festivalClock.now());
    this.webgl.toneMappingExposure = this.sky.exposureTarget;

    // Seed camera somewhere reasonable so spawn-picker frames have something
    // visible behind them.
    this.camera.position.set(60, 30, 60);
    this.camera.lookAt(0, 5, 0);

    // Post-processing pipeline (bloom)
    if (this.quality.bloom) {
      this.postFx = new PostFX(this.webgl, this.scene, this.camera);
      this.postFx.resize(window.innerWidth, window.innerHeight);
    }

    this.input.attach(this.canvas);
    this.resize();
    window.addEventListener('resize', this.resize);
  }

  /** Spawn the local player into a zone. Does NOT auto-connect multiplayer. */
  spawnInto(zoneId: string): void {
    const sp = this.world.spawns[zoneId];
    if (sp) {
      this.player.setSpawn(sp.x, sp.z);
      this.player.yaw = sp.yaw;
    } else {
      const zone = ZONES[zoneId] ?? ZONES.arch;
      this.player.setSpawn(zone.center[0], zone.center[1]);
    }
    this.currentZoneId = zoneId;
    useUI.getState().setCurrentZone(zoneId);
    // Multiplayer is opt-in via renderer.enableMultiplayer() so we don't burn
    // Cloudflare DO requests for solo walkabouts.
  }

  /** Explicit opt-in to live multiplayer. Connects to the current zone's DO. */
  enableMultiplayer(): void {
    if (!this.currentZoneId) return;
    this.handoffTo(this.currentZoneId);
  }

  /** Explicit opt-out — close WebSocket and forget. */
  disableMultiplayer(): void {
    this.net.disconnect();
  }

  private handoffTo(zoneId: string): void {
    if (this.currentZoneId === zoneId) return;
    this.currentZoneId = zoneId;
    const id = useUI.getState().identity;
    if (id) this.net.setIdentity(id.displayName, id.hatColor);
    this.net.connectTo(zoneId);
    useUI.getState().setCurrentZone(zoneId);
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    this.clock.start();
    this.tick();
  }

  stop(): void {
    this.running = false;
  }

  private resize = (): void => {
    const w = window.innerWidth;
    const h = window.innerHeight;
    this.webgl.setSize(w, h, false);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    this.postFx?.resize(w, h);
  };

  private tick = (): void => {
    if (!this.running) return;
    const dt = Math.min(0.1, this.clock.getDelta());

    // Update sky every 1s
    this.skyTickAcc += dt;
    if (this.skyTickAcc > 0.5) {
      this.skyTickAcc = 0;
      const ftime = festivalClock.now();
      this.sky.update(ftime);
      this.webgl.toneMappingExposure = THREE.MathUtils.lerp(
        this.webgl.toneMappingExposure,
        this.sky.exposureTarget,
        0.3
      );
      useUI.getState().setFestivalTimeLabel(formatFestivalTime(ftime));
    }

    // Player + camera
    this.player.update(dt);

    // Only send position + do zone handoff while multiplayer is active.
    if (this.net.connected) {
      this.net.sendPosition(
        this.player.position.x,
        this.player.position.y,
        this.player.position.z,
        this.player.avatar.group.rotation.y,
        this.player.currentAnim()
      );
      const inZone = zoneAt(this.player.position.x, this.player.position.z);
      if (inZone && inZone.id !== this.currentZoneId?.replace(/-\d+$/, '')) {
        this.handoffTo(inZone.id);
      }
    } else {
      // Still update UI 'current zone' label for solo walks
      const inZone = zoneAt(this.player.position.x, this.player.position.z);
      if (inZone && inZone.id !== this.currentZoneId) {
        this.currentZoneId = inZone.id;
        useUI.getState().setCurrentZone(inZone.id);
      }
    }

    // Animated landmarks
    for (const a of this.world.animated) a.update(dt);

    // Remote players
    this.remotes.update(dt);

    // Stage screen overlay positions
    this.screens.update();

    // Audio scheduler tick (~1Hz)
    this.audioTickAcc += dt;
    if (this.audioTickAcc > 1) {
      this.audioTickAcc = 0;
      this.audio.tick();
    }

    if (this.postFx) {
      this.postFx.render();
    } else {
      this.webgl.render(this.scene, this.camera);
    }
    requestAnimationFrame(this.tick);
  };
}
